class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawer = this.querySelector('#CartDrawer');
    this.overlay = this.querySelector('#CartDrawer-Overlay');
    this.inner = this.querySelector('.drawer__inner');

    this._cbhScrollClass = 'overflow-hidden';

    // Bind once
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.onClick = this.onClick.bind(this);

    this.addEventListener('keyup', this.onKeyUp);
    this.addEventListener('click', this.onClick);
    if (this.overlay) this.overlay.addEventListener('click', this.onOverlayClick);

    this.setHeaderCartIconAccessibility();
  }

  onKeyUp(evt) {
    if (evt.code && evt.code.toUpperCase() === 'ESCAPE') this.close();
  }

  onOverlayClick() {
    this.close();
  }

  onClick(evt) {
    // Close drawer when clicking a close button (works even after HTML re-render)
    const closeBtn = evt.target && evt.target.closest ? evt.target.closest('.drawer__close') : null;
    if (!closeBtn) return;

    evt.preventDefault();
    this.close();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code && event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Ensure viewport height var stays correct on mobile
    try {
      if (window.matchMedia('(max-width: 989px)').matches) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } catch (e) {}

    // Animate open
    window.setTimeout(() => {
      this.classList.add('animate', 'active');
    }, 0);

    // Lock scroll (prefer your ref-counted CBH lock if present)
    this.lockScroll();

    // Trap focus without relying on transitionend (which can be flaky)
    this.trapFocusSoon();
  }

  trapFocusSoon() {
    const doTrap = () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : (this.drawer || this);

      const focusElement =
        this.querySelector('.drawer__close') ||
        this.querySelector('.drawer__inner') ||
        containerToTrapFocusOn;

      try {
        trapFocus(containerToTrapFocusOn, focusElement);
      } catch (e) {}
    };

    // Run immediately, then once more after next paint to catch freshly-rendered content
    requestAnimationFrame(() => {
      doTrap();
      requestAnimationFrame(doTrap);
    });
  }

  close() {
    this.classList.remove('active');

    try {
      removeTrapFocus(this.activeElement);
    } catch (e) {}

    this.unlockScroll();
  }

  lockScroll() {
    // Prefer your CBH_SCROLL_LOCK if available (from global.js)
    if (window.CBH_SCROLL_LOCK && typeof window.CBH_SCROLL_LOCK.lock === 'function') {
      window.CBH_SCROLL_LOCK.lock(this._cbhScrollClass);
      return;
    }

    document.body.classList.add(this._cbhScrollClass);
  }

  unlockScroll() {
    if (window.CBH_SCROLL_LOCK && typeof window.CBH_SCROLL_LOCK.unlock === 'function') {
      window.CBH_SCROLL_LOCK.unlock(this._cbhScrollClass);
      return;
    }

    document.body.classList.remove(this._cbhScrollClass);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Keep drawer open state stable (no re-open flicker)
    this.productId = parsedState.id;

    // Ensure drawer isn't marked empty
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!sectionElement) return;

      const nextHTML = parsedState.sections && parsedState.sections[section.id];
      if (!nextHTML) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(nextHTML, section.selector);
    });

    // Re-trap focus after DOM replacement if drawer is currently open
    if (this.classList.contains('active')) this.trapFocusSoon();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
